import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GitBranch, Plus, X, UserCircle, PencilSimple } from "@phosphor-icons/react";

const EMPTY_DRAFT = { name: "", system: "__any__", region: "__any__", issue_type: "__any__", team: "__any__", user_ids: [] };

/**
 * AssignmentRulesPanel — ordered list of `system|region|issue_type →
 * agent-pool` rules evaluated first-match-wins before falling back
 * to `auto_assign_mode` (round-robin / least-loaded). A rule matches
 * when every non-null field equals the ticket's value. At least one
 * field must be filled (backend refuses catch-all rules).
 *
 * Each rule can point at MULTIPLE agents — within the matched pool
 * the backend picks using the workspace's auto-assign mode (round-
 * robin per rule / least-loaded).
 */
export function AssignmentRulesPanel({ settings, setSettings, users, isAdmin }) {
  // `editingId` — `null` = idle, `"new"` = adding, otherwise the rule
  // id currently being edited. The draft holds pending changes and is
  // committed by the local Save button (which also exits edit mode).
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [pickerOpen, setPickerOpen] = useState(false);
  const rules = settings.assignment_rules || [];
  const systems = settings.systems || [];
  const issueTypes = settings.issue_types || [];
  const regionNames = (settings.regions || []).map((r) => (typeof r === "string" ? r : r.name)).filter(Boolean);
  const agents = users.filter((u) => u.role !== "requester" && (u.is_active ?? true));

  const ruleUsers = (r) => {
    // Back-compat — old rules may still have a scalar user_id until
    // the admin re-saves them. Treat that as a single-user pool.
    if (Array.isArray(r.user_ids) && r.user_ids.length) return r.user_ids;
    if (r.user_id) return [r.user_id];
    return [];
  };
  const nameFor = (uid) => agents.find((u) => u.id === uid)?.name || "(deleted user)";

  const startNew = () => {
    setEditingId("new");
    setDraft(EMPTY_DRAFT);
    setPickerOpen(false);
  };
  const startEdit = (r) => {
    setEditingId(r.id);
    setDraft({
      name: r.name || "",
      system: r.system || "__any__",
      region: r.region || "__any__",
      issue_type: r.issue_type || "__any__",
      team: r.team || "__any__",
      user_ids: [...ruleUsers(r)],
    });
    setPickerOpen(false);
  };
  const cancel = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setPickerOpen(false);
  };

  const toggleDraftUser = (uid) => {
    setDraft((d) => ({
      ...d,
      user_ids: d.user_ids.includes(uid)
        ? d.user_ids.filter((x) => x !== uid)
        : [...d.user_ids, uid],
    }));
  };
  const removeDraftUser = (uid) => setDraft((d) => ({ ...d, user_ids: d.user_ids.filter((x) => x !== uid) }));

  const commit = () => {
    const sys = draft.system === "__any__" ? null : draft.system;
    const reg = draft.region === "__any__" ? null : draft.region;
    const itype = draft.issue_type === "__any__" ? null : draft.issue_type;
    const team = draft.team === "__any__" ? null : draft.team;
    if (!sys && !reg && !itype && !team) return;
    if (!draft.user_ids.length) return;
    const payload = {
      name: draft.name.trim() || "",
      system: sys, region: reg, issue_type: itype, team,
      user_ids: [...draft.user_ids],
    };
    if (editingId === "new") {
      const next = { id: crypto.randomUUID(), ...payload };
      setSettings({ ...settings, assignment_rules: [...rules, next] });
    } else {
      setSettings({
        ...settings,
        assignment_rules: rules.map((r) => (r.id === editingId ? { ...r, ...payload } : r)),
      });
    }
    cancel();
  };
  const remove = (id) => setSettings({ ...settings, assignment_rules: rules.filter((r) => r.id !== id) });
  const move = (id, dir) => {
    const idx = rules.findIndex((r) => r.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= rules.length) return;
    const copy = rules.slice();
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    setSettings({ ...settings, assignment_rules: copy });
  };

  const isEditing = editingId !== null;
  const isAdding = editingId === "new";

  return (
    <div className="hairline rounded-lg p-5 bg-white space-y-3" data-testid="assignment-rules-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <GitBranch size={16} weight="duotone" className="text-[#4f4c90]" />
          <div className="font-heading font-semibold">Assignment rules</div>
        </div>
        {isAdmin && !isEditing && (
          <Button onClick={startNew} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-8 gap-1 text-xs" data-testid="assignment-rule-new">
            <Plus size={12} weight="bold" /> New rule
          </Button>
        )}
      </div>
      <div className="text-xs text-slate-500">
        Route new tickets to a specific set of agents based on queue (IT / Systems), system, region, and/or request type.
        Rules run top-to-bottom, first match wins. Within a matched rule the assignee is picked
        using the auto-assign mode above (round-robin per rule or least-loaded). Anything that
        doesn't match falls back to the plain auto-assign.
      </div>

      {rules.length === 0 && !isAdding ? (
        <div className="text-[11px] text-slate-500 italic border border-dashed border-slate-200 rounded-md px-3 py-4 text-center">
          No rules yet — every ticket falls back to auto-assign.
        </div>
      ) : (
        <div className="space-y-1.5">
          {rules.map((r, i) => {
            if (editingId === r.id) {
              return (
                <RuleEditor
                  key={r.id}
                  index={i}
                  draft={draft}
                  setDraft={setDraft}
                  systems={systems}
                  regionNames={regionNames}
                  issueTypes={issueTypes}
                  agents={agents}
                  pickerOpen={pickerOpen}
                  setPickerOpen={setPickerOpen}
                  toggleDraftUser={toggleDraftUser}
                  removeDraftUser={removeDraftUser}
                  onSave={commit}
                  onCancel={cancel}
                  saveLabel="Save changes"
                />
              );
            }
            const uids = ruleUsers(r);
            return (
              <div key={r.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5 bg-slate-50/60" data-testid={`assignment-rule-${r.id}`}>
                <span className="font-mono text-[11px] text-slate-500 w-5">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800">
                    {r.name ? <b>{r.name}</b> : <span className="italic text-slate-500">(unnamed)</span>}
                    <span className="text-slate-500"> · </span>
                    <span className="font-mono text-xs">
                      {r.team ? <span className="text-[#4f4c90] font-semibold">queue={r.team === "it" ? "IT" : "Systems"}</span> : "queue=any"}
                      {" · "}
                      {r.system ? `system=${r.system}` : "system=any"}
                      {" · "}
                      {r.region ? `region=${r.region}` : "region=any"}
                      {r.issue_type ? ` · type=${r.issue_type}` : ""}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-[11px] text-slate-500 mr-1">→</span>
                    {uids.length === 0 && (
                      <span className="text-[11px] italic text-amber-700">No agents — rule inactive</span>
                    )}
                    {uids.map((uid) => (
                      <span key={uid} className="chip !text-[#4f4c90] !border-[#4f4c90]/30 bg-[#4f4c90]/10 flex items-center gap-1 !text-[11px] !py-0.5" data-testid={`rule-user-${r.id}-${uid}`}>
                        <UserCircle size={10} weight="duotone" />
                        {nameFor(uid)}
                      </span>
                    ))}
                    {uids.length > 1 && (
                      <span className="text-[10px] text-slate-500 italic self-center">
                        ({uids.length} agents — auto-assigned)
                      </span>
                    )}
                  </div>
                </div>
                {isAdmin && !isEditing && (
                  <>
                    <button type="button" onClick={() => move(r.id, -1)} disabled={i === 0} className="text-xs text-slate-500 hover:text-[#4f4c90] disabled:opacity-30" title="Move up">↑</button>
                    <button type="button" onClick={() => move(r.id, 1)} disabled={i === rules.length - 1} className="text-xs text-slate-500 hover:text-[#4f4c90] disabled:opacity-30" title="Move down">↓</button>
                    <button type="button" onClick={() => startEdit(r)} className="text-slate-500 hover:text-[#4f4c90]" title="Edit" data-testid={`assignment-rule-edit-${r.id}`}>
                      <PencilSimple size={13} weight="duotone" />
                    </button>
                    <button type="button" onClick={() => remove(r.id)} className="text-slate-500 hover:text-red-500" title="Remove" data-testid={`assignment-rule-delete-${r.id}`}>
                      <X size={12} weight="bold" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isAdmin && isAdding && (
        <RuleEditor
          index={rules.length}
          draft={draft}
          setDraft={setDraft}
          systems={systems}
          regionNames={regionNames}
          issueTypes={issueTypes}
          agents={agents}
          pickerOpen={pickerOpen}
          setPickerOpen={setPickerOpen}
          toggleDraftUser={toggleDraftUser}
          removeDraftUser={removeDraftUser}
          onSave={commit}
          onCancel={cancel}
          saveLabel="Add rule"
        />
      )}
    </div>
  );
}


/**
 * RuleEditor — used for both "add" and "inline edit". Contained here
 * (not in its own file) because it only makes sense inside the rules
 * panel and shares its draft state.
 */
function RuleEditor({
  index, draft, setDraft, systems, regionNames, issueTypes, agents,
  pickerOpen, setPickerOpen, toggleDraftUser, removeDraftUser,
  onSave, onCancel, saveLabel,
}) {
  const disabled = !draft.user_ids.length
    || (draft.system === "__any__" && draft.region === "__any__" && draft.issue_type === "__any__" && draft.team === "__any__");
  const nameFor = (uid) => agents.find((u) => u.id === uid)?.name || "(deleted user)";
  return (
    <div className="border border-[#4f4c90]/30 rounded-md p-3 bg-[#4f4c90]/5 space-y-2" data-testid="assignment-rule-editor">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium">
          Rule {index + 1}
        </div>
        <button type="button" onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-800" data-testid="assignment-rule-editor-cancel">Cancel</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        <Input
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Rule name (optional)"
          className="bg-white border-slate-200 h-9"
          data-testid="rule-name"
        />
        <Select value={draft.team} onValueChange={(v) => setDraft({ ...draft, team: v })}>
          <SelectTrigger className="bg-white border-slate-200 h-9" data-testid="rule-team"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="__any__">Queue: any</SelectItem>
            <SelectItem value="systems">Business Systems</SelectItem>
            <SelectItem value="it">IT</SelectItem>
          </SelectContent>
        </Select>
        <Select value={draft.system} onValueChange={(v) => setDraft({ ...draft, system: v })}>
          <SelectTrigger className="bg-white border-slate-200 h-9" data-testid="rule-system"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="__any__">System: any</SelectItem>
            {systems.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={draft.region} onValueChange={(v) => setDraft({ ...draft, region: v })}>
          <SelectTrigger className="bg-white border-slate-200 h-9" data-testid="rule-region"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="__any__">Region: any</SelectItem>
            {regionNames.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={draft.issue_type} onValueChange={(v) => setDraft({ ...draft, issue_type: v })}>
          <SelectTrigger className="bg-white border-slate-200 h-9" data-testid="rule-issue-type"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="__any__">Request type: any</SelectItem>
            {issueTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="relative">
        <div className="flex items-center gap-2 flex-wrap border border-slate-200 rounded-md px-2 py-2 bg-white min-h-10" data-testid="rule-users">
          {draft.user_ids.length === 0 && (
            <span className="text-[11px] text-slate-500 italic">Assign to one or more agents…</span>
          )}
          {draft.user_ids.map((uid) => (
            <span key={uid} className="chip !text-[#4f4c90] !border-[#4f4c90]/30 bg-[#4f4c90]/10 flex items-center gap-1" data-testid={`rule-user-selected-${uid}`}>
              <UserCircle size={11} weight="duotone" />
              {nameFor(uid)}
              <button type="button" onClick={() => removeDraftUser(uid)} className="hover:text-red-500" title="Remove"><X size={11} weight="bold" /></button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            className="text-xs text-[#4f4c90] hover:underline flex items-center gap-1"
            data-testid="rule-add-user-toggle"
          >
            <Plus size={11} weight="bold" /> Add agent
          </button>
        </div>
        {pickerOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setPickerOpen(false)} />
            <div
              className="absolute z-50 mt-1 w-64 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg p-1"
              data-testid="rule-user-picker"
            >
              {agents.length === 0 && <div className="text-[11px] text-slate-500 italic px-2 py-1">No active agents.</div>}
              {agents.map((u) => {
                const on = draft.user_ids.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleDraftUser(u.id)}
                    className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 text-xs ${on ? "bg-[#4f4c90]/10 text-[#4f4c90]" : "hover:bg-slate-100"}`}
                    data-testid={`rule-user-picker-${u.id}`}
                  >
                    <input type="checkbox" checked={on} readOnly className="accent-[#4f4c90]" />
                    <span className="flex-1 truncate">{u.name}</span>
                    <span className="text-[10px] text-slate-500">{u.role}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          onClick={onSave}
          disabled={disabled}
          className="bg-[#39A36E] hover:bg-[#2f8c5d] h-9 gap-1"
          data-testid="assignment-rule-save"
        >
          <Plus size={12} weight="bold" /> {saveLabel}
        </Button>
        <div className="text-[10px] text-slate-500 italic">
          Rule must match at least one of queue, system, region, or request type — catch-all rules are refused.
          Pick multiple agents to spread load / cover leave.
        </div>
      </div>
    </div>
  );
}
